/**
 * SOLID Review
 * S — Strong. One concern: what a lift shows the table — the reveal it keeps, the dice on screen, and the phase it
 *     leaves the table in, live, missed or found on a return.
 * O — Good. A new fact the table keeps of a reveal is one key in `stored` and its schema; a new way to meet a reveal
 *     is one more phase question.
 * L — N/A — no inheritance hierarchy (private constructor).
 * I — Strong. A factory, the stored form, the dice, three phase questions.
 * D — Strong. The Domain's phases, the server's dice ranking and the schema types.
 */
import { GamePhase } from '../uml-components/primitives';
import type { DiceRanking } from '../dice/DiceRanking';
import type { IEntityClientDiceRoll, IEntityClientReveal } from '../ports/schemas/ClientTable';
import type { IPayloadReveal } from '../ports/schemas/TableEvents';

/**
 * A LIFT'S REVEAL, AS A VALUE OBJECT (frontend-domain-oo P3, 2026-10-26; handbook §28.270) — `revealOf`
 * (`decisions/table/tableDraft.ts`) and `game/RevealTransforms.ts`, which lifted these transformations out of
 * ControlPanel's subscription callbacks (2026-08-17) and moved them to the Domain in FE1 (2026-10-03), on the
 * reveal they read. Three ways the table meets a reveal, three phases:
 *
 *   - the lift's own event: the game over when the server says so (`phaseAfterLift`);
 *   - a reveal a pushed view shows the client missed: on screen as the lift would have put it (`phaseWhenMissed`);
 *   - a reveal found on a return to the table: the loser's screen, or the next round's wait (`phaseOnReturn`).
 */
export class Reveal {
  private constructor(private readonly reveal: IPayloadReveal) {}

  /** A lift's outcome as the server sends it — the cup-lift event, or the active round's. */
  static of(reveal: IPayloadReveal): Reveal {
    return new Reveal(reveal);
  }

  /** A reveal as the table stores it — the seven fields the screen and Play Again read, no more. */
  stored(): IEntityClientReveal {
    return {
      losingPlayerId: this.reveal.losingPlayerId,
      wasBluff: this.reveal.wasBluff,
      claimedScore: this.reveal.claimedScore,
      trueScore: this.reveal.trueScore,
      die1: this.reveal.die1,
      die2: this.reveal.die2,
      newRollerId: this.reveal.newRollerId,
    };
  }

  /** Both dice are set: the cup-lift event puts dice on screen only then, as ControlPanel's callback did. */
  hasDice(): boolean {
    return Boolean(this.reveal.die1 && this.reveal.die2);
  }

  /**
   * The dice on screen once the cup is lifted. This existed TWICE in ControlPanel, byte for byte — seeding a rejoin
   * mid-REVEALING, and handling the live cup-lift — so a change to the classification could reach one path and not
   * the other.
   *
   * MEYER AND A PAIR ARE THE SERVER'S WORD (frontend-domain-oo P2): the reveal's true score is looked up in the
   * ranking. Until P2 `isDouble` was the range `>= 110 && < 1000`, copied from an older backend peek that counted
   * Little Meyer (900) a double; the backend's peek has answered `isPair()` since domain-oo, so the same dice were
   * a double in the client's reveal and not in the server's peek (§28.268's finding). No rule or screen in either
   * workspace reads `isDouble` yet: it is written, carried and asserted.
   */
  diceRoll(ranking: DiceRanking): IEntityClientDiceRoll {
    return {
      die1: this.reveal.die1,
      die2: this.reveal.die2,
      score: this.reveal.trueScore,
      isMeyer: ranking.isMeyer(this.reveal.trueScore),
      isDouble: ranking.isDouble(this.reveal.trueScore),
    };
  }

  /**
   * THE SERVER SAYS WHETHER THE GAME IS OVER (backend §28.191). The table decides it (the first player out,
   * handbook §3.4) and records it on the reveal; a client that judged it from its OWN copy of the lives —
   * decremented locally on every lift — called it wrong after a single missed event. Only a reveal from an older
   * server, which carries no `endsGame`, falls back to the loser's count after the lift: none left ends the game.
   *
   * The count is always a number here — the server's, or the one held less one (`TableLives.leftAfterLosing`).
   * `phaseAfterCupLift` defaulted a missing count to 1, "still alive", for a caller that could pass none; its one
   * caller never did (§28.267's finding), and the default went with the function in P3.
   */
  phaseAfterLift(livesLeft: number): GamePhase {
    if (this.reveal.endsGame === true) return GamePhase.GAME_OVER;
    if (this.reveal.endsGame === false) return GamePhase.REVEALING;
    return livesLeft <= 0 ? GamePhase.GAME_OVER : GamePhase.REVEALING;
  }

  /** A reveal the client missed, put on screen as the cup-lift event does for every seat: game over when the table says so. */
  phaseWhenMissed(): GamePhase {
    return this.reveal.endsGame === true ? GamePhase.GAME_OVER : GamePhase.REVEALING;
  }

  /**
   * A reveal found on a return to the table: on screen for everyone, and REVEALING only for the loser, who holds
   * the Roll First button; everyone else waits in ROLLING for the next round.
   */
  phaseOnReturn(playerId: string): GamePhase {
    return this.reveal.losingPlayerId === playerId ? GamePhase.REVEALING : GamePhase.ROLLING;
  }
}
